import { useCustom } from '@refinedev/core';
import { Drawer, Table, Tag, Button, Spin } from 'antd';
import { ExternalLink, Download } from 'lucide-react';
import type { FC } from 'react';

interface Invoice {
  invoiceId: string;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  created: string;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

interface Props { subscriptionId: string | null; onClose: () => void }

const STATUS_COLOR: Record<string, string> = { paid: 'success', open: 'processing', draft: 'default', void: 'default', uncollectible: 'error' };

const InvoiceDrawer: FC<Props> = ({ subscriptionId, onClose }) => {
  const { data, isLoading } = useCustom<{ data: { invoices: Invoice[] } }>({
    url: `/api/v1/admin/billing/subscriptions/${subscriptionId}`,
    method: 'get',
    queryOptions: { enabled: !!subscriptionId },
  });

  const invoices = data?.data?.data?.invoices ?? [];

  return (
    <Drawer title="Factures Stripe" open={!!subscriptionId} onClose={onClose} width={520} destroyOnClose>
      {isLoading ? (
        <div className="flex h-32 items-center justify-center"><Spin /></div>
      ) : (
        <Table dataSource={invoices} rowKey="invoiceId" size="small" pagination={false}>
          <Table.Column dataIndex="created" title="Date" width={100}
            render={(d: string) => <span className="text-xs text-slate-600 dark:text-slate-400">{d ? new Date(d).toLocaleDateString('fr-FR') : '—'}</span>} />
          <Table.Column dataIndex="amountDue" title="Montant" width={90}
            render={(a: number, r: Invoice) => <span className="font-medium text-slate-900 dark:text-white">{(a / 100).toLocaleString('fr-FR', { style: 'currency', currency: r.currency || 'eur' })}</span>} />
          <Table.Column dataIndex="status" title="Statut" width={90}
            render={(s: string) => <Tag color={STATUS_COLOR[s] ?? 'default'}>{s}</Tag>} />
          <Table.Column title="" width={70} render={(_: unknown, r: Invoice) => (
            <div className="flex gap-1">
              {r.hostedInvoiceUrl && <Button type="text" size="small" icon={<ExternalLink className="h-3.5 w-3.5" />} href={r.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" />}
              {r.invoicePdf && <Button type="text" size="small" icon={<Download className="h-3.5 w-3.5" />} href={r.invoicePdf} target="_blank" rel="noopener noreferrer" />}
            </div>
          )} />
        </Table>
      )}
    </Drawer>
  );
};

export default InvoiceDrawer;
